/**
 * P12.4 — Project Review Gate + Planning Override Archive
 *
 * Types for the review status carried on Job, the append-only override
 * archive, and the resolved approved-plan view.
 *
 * Hard rules:
 *   - System estimate is never overwritten.
 *   - Override never mutates Job.scheduledShifts / payoutAmount silently;
 *     the resolver overlays final values on top of system values.
 *   - No contractValue exposure here. Ever.
 */

export type ReviewStatus = "pending" | "approved";

export interface JobPlanningReview {
  status: ReviewStatus;
  approvedBy?: string;
  approvedAt?: string;
  archiveEntryId?: string;
}

export type PlanningOverrideReasonCategory =
  | "missed_condition_at_consultation"
  | "material_constraint"
  | "access_logistics_issue"
  | "customer_specific_requirement"
  | "sales_input_error"
  | "pm_judgment_field_experience"
  | "other";

export const PLANNING_OVERRIDE_REASON_LABEL: Record<
  PlanningOverrideReasonCategory,
  string
> = {
  missed_condition_at_consultation: "Missed condition at consultation",
  material_constraint: "Material constraint",
  access_logistics_issue: "Access / logistics issue",
  customer_specific_requirement: "Customer-specific requirement",
  sales_input_error: "Sales input error",
  pm_judgment_field_experience: "PM judgment / field experience",
  other: "Other",
};

export interface PlanningSystemEstimateSnapshot {
  days: number;
  crewSize: number;
  payoutAmount: number;
  riskFlags: string[];
  arrival: string;
  returnVisit?: string;
}

export interface PlanningOverrideValues {
  days?: number;
  crewSize?: number;
  payoutAmount?: number;
  arrivalNote?: string;
}

export interface PlanningFinalApproved {
  days: number;
  crewSize: number;
  payoutAmount: number;
  arrivalNote?: string;
}

export interface PlanningInputsSnapshot {
  projectType: string;
  permitRequired: boolean;
  scopeSummary: string;
  /** Lightweight projectConfig copy — structured fields only. */
  projectConfig?: Record<string, unknown>;
}

export interface PlanningJobSpecSnapshot {
  specId?: string;
  label?: string;
  phaseCount: number;
}

export interface PlanningOverrideArchiveEntry {
  id: string;
  jobId: string;
  createdAt: string;
  createdBy: string;
  systemEstimate: PlanningSystemEstimateSnapshot;
  override: PlanningOverrideValues;
  finalApproved: PlanningFinalApproved;
  reasonCategory: PlanningOverrideReasonCategory;
  reasonNote: string;
  inputsSnapshot: PlanningInputsSnapshot;
  jobSpecSnapshot: PlanningJobSpecSnapshot;
}

export interface ApprovedProjectPlan {
  systemPlan: PlanningSystemEstimateSnapshot;
  overridePlan?: PlanningOverrideValues;
  finalPlan: PlanningFinalApproved;
  reviewStatus: ReviewStatus;
  wasOverridden: boolean;
  approvedBy?: string;
  approvedAt?: string;
  reviewedAt?: string;
  archiveEntryId?: string;
}
